import {
  ActionsEnum,
  AdvancedFilterField,
  ColumnData,
  PathRoute,
  RoutingDataLight,
} from '@i-supplier/angular-shared-module';
import {AuthGuard} from '../core/guards/auth.guard';
import {ActivitySheetListComponent} from './activity-sheet-list/activity-sheet-list.component';
import {ActivitySheetStatusEnum} from './models/activity-sheet.model';

export enum ColumnsEnum {
  id = 'id',
  projectName = 'projectName',
  purchaseOrderNumber = 'purchaseOrderNumber',
  buyerCompanyName = 'buyerCompanyName',
  supplierCompanyName = 'supplierCompanyName',
  employeeName = 'employeeName',
  startDate = 'startDate',
  submissionDate = 'submissionDate',
  validationDate = 'validationDate',
  sum = 'sum',
  period = 'period',
  status = 'status',
}

const baseFilters = [
  ColumnsEnum.projectName,
  ColumnsEnum.purchaseOrderNumber,
  ColumnsEnum.buyerCompanyName,
  ColumnsEnum.supplierCompanyName,
  ColumnsEnum.employeeName,
  ColumnsEnum.period,
  ColumnsEnum.submissionDate,
  ColumnsEnum.validationDate,
];
const filters = [...baseFilters];
const filtersAll = [...baseFilters, ColumnsEnum.status];
export const initialColumns: ColumnData[] = [
  {name: ColumnsEnum.id, sortable: true, key: 'global.common.id'},
  {name: ColumnsEnum.projectName, sortable: true, key: 'global.common.project', title: true},
  {name: ColumnsEnum.purchaseOrderNumber, sortable: true, key: 'global.common.po-num'},
  {name: ColumnsEnum.buyerCompanyName, sortable: true, key: 'global.common.buyer'},
  {name: ColumnsEnum.supplierCompanyName, sortable: true, key: 'global.common.supplier'},
  {name: ColumnsEnum.employeeName, sortable: true, key: 'global.common.provider'},
  {name: ColumnsEnum.startDate, sortable: true, key: 'global.common.period'},
  {name: ColumnsEnum.submissionDate, sortable: true, key: 'global.common.date-submission'},
  {name: ColumnsEnum.validationDate, sortable: true, key: 'global.common.date-validation'},
  {name: ColumnsEnum.sum, sortable: false, key: 'global.common.quantity'},
];
export const columns: ColumnData[] = [
  ...initialColumns,
  {name: ActionsEnum.actions},
];

function findColumn(name: ColumnsEnum) {
  return columnsAll.find(column => column.name === name);
}

export const columnsAll: ColumnData[] = [
  ...initialColumns,
  {name: ColumnsEnum.status, sortable: true, key: 'global.common.status'},
  {name: ActionsEnum.actions},
];
export const AdvancedFilterData: AdvancedFilterField[] = [
  {
    name: ColumnsEnum.projectName,
    control: ColumnsEnum.projectName,
    label: () => findColumn(ColumnsEnum.projectName).key,
    type: 'text',
  },
  {
    name: ColumnsEnum.purchaseOrderNumber,
    control: ColumnsEnum.purchaseOrderNumber,
    label: () => findColumn(ColumnsEnum.purchaseOrderNumber).key,
    type: 'text',
  },
  {
    name: ColumnsEnum.buyerCompanyName,
    control: ColumnsEnum.buyerCompanyName,
    label: () => findColumn(ColumnsEnum.buyerCompanyName).key,
    type: 'text',
  },
  {
    name: ColumnsEnum.supplierCompanyName,
    control: ColumnsEnum.supplierCompanyName,
    label: () => findColumn(ColumnsEnum.supplierCompanyName).key,
    type: 'text',
  },
  {
    name: ColumnsEnum.employeeName,
    control: ColumnsEnum.employeeName,
    label: () => findColumn(ColumnsEnum.employeeName).key,
    type: 'text',
  },
  {
    name: ColumnsEnum.sum,
    control: ColumnsEnum.sum,
    label: () => findColumn(ColumnsEnum.sum).key,
    type: 'number',
  },
  {
    name: ColumnsEnum.period,
    control: ColumnsEnum.period,
    label: () => 'global.common.period',
    type: 'rangeDate',
    configs: {
      range: true,
      dateInputFormat: 'DD-MM-YYYY',
      containerClass: 'theme-blue',
      showClearButton: true,
      isAnimated: true,
      readonly: true,
      showPreviousMonth: true,
    },
  },
  {
    name: ColumnsEnum.submissionDate,
    control: ColumnsEnum.submissionDate,
    label: () => findColumn(ColumnsEnum.submissionDate).key,
    type: 'rangeDate',
    configs: {
      range: true,
      dateInputFormat: 'DD-MM-YYYY',
      containerClass: 'theme-blue',
      showClearButton: true,
      isAnimated: true,
      readonly: true,
      showPreviousMonth: true,
    },
  },
  {
    name: ColumnsEnum.validationDate,
    control: ColumnsEnum.validationDate,
    label: () => findColumn(ColumnsEnum.validationDate).key,
    type: 'rangeDate',
    configs: {
      range: true,
      dateInputFormat: 'DD-MM-YYYY',
      containerClass: 'theme-blue',
      showClearButton: true,
      isAnimated: true,
      readonly: true,
      showPreviousMonth: true,
    },
  },
  {
    name: ColumnsEnum.status,
    control: ColumnsEnum.status,
    label: () => findColumn(ColumnsEnum.status).key,
    type: 'select',
    items: Object.values(ActivitySheetStatusEnum)
      .map(item => ({
        value: item,
        label: `activity-report.status.${item}`,
    })),
    configs: {
      clearable: true,
      multiple: true,
      searchable: true,
      closeOnSelect: false,
      placeholder: 'global.filters.status',
      hideSelected: false,
      bindValue: 'value',
    },
  },
];
export const tabConfig: RoutingDataLight = {
  columns: columns,
  filters: filters,
  menuList: generateMenu('activity-report', ['all', ActivitySheetStatusEnum.DRAFT,
    ActivitySheetStatusEnum.SUBMITTED,
    ActivitySheetStatusEnum.VALIDATED,
    ActivitySheetStatusEnum.REFUSED,
    ActivitySheetStatusEnum.CANCELLED]),
};
export const allConfig: RoutingDataLight = {
  columns: columnsAll,
  filters: filtersAll,
  menuList: generateMenu('activity-report', ['all', ActivitySheetStatusEnum.DRAFT,
    ActivitySheetStatusEnum.SUBMITTED,
    ActivitySheetStatusEnum.VALIDATED,
    ActivitySheetStatusEnum.REFUSED,
    ActivitySheetStatusEnum.CANCELLED]),
};

function generateMenu(title: string, status: string[]): any[] {
  const menu = [];
  menu.push(...status.map(item => ({
    link: `/${title}/${item.toLowerCase()}`,
    title: item === 'all' ? 'global.common.all' : `activity-report.status.${item}`,
  })));
  return menu;
}

export type PathRoutes = PathRoute[];

export function generateRoute(
  initial: RoutingDataLight,
  status: ActivitySheetStatusEnum,
  name: string,
): PathRoute {
  let routeFilters = Object.assign([], initial.filters);
  let routeColumns = Object.assign([], initial.columns);
  switch (status) {
    case ActivitySheetStatusEnum.DRAFT:
      routeFilters = filters?.filter(value => value !== ColumnsEnum.submissionDate && value !== ColumnsEnum.validationDate);
      routeColumns = routeColumns?.filter(value => value.name !== ColumnsEnum.submissionDate && value.name !== ColumnsEnum.validationDate);
      break;
    case ActivitySheetStatusEnum.SUBMITTED:
    case ActivitySheetStatusEnum.REFUSED:
      routeFilters = filters?.filter(value => value !== ColumnsEnum.validationDate);
      routeColumns = routeColumns?.filter(value => value.name !== ColumnsEnum.validationDate);
      break;
  }
  return {
    path: name.toLowerCase(),
    component: ActivitySheetListComponent,
    canActivate: [AuthGuard],
    data: {
      search: true,
      columns: routeColumns,
      filters: routeFilters,
      menuList: initial.menuList,
      title: initial.title + '.' + name,
      status: status,
    },
  };
}
